import React, { useState } from 'react';
import { Card, CardBody, CardHeader, Divider } from '@heroui/react';
import { EntityInstance } from '../types';
import PropertyCard from './PropertyCard';
import RelationCard from './RelationCard';

interface EntityTreeViewProps {
  entity: EntityInstance;
  searchQuery?: string;
  registerMatchRef?: (ref: HTMLDivElement | null, relationName?: string) => void;
  currentMatchIndex?: number;
}

const EntityTreeView: React.FC<EntityTreeViewProps> = ({ entity, searchQuery, registerMatchRef }) => {
  const [expandedRelations, setExpandedRelations] = useState<Record<string, boolean>>({});

  const toggleRelation = (relationName: string) => {
    setExpandedRelations(prev => ({
      ...prev,
      [relationName]: !prev[relationName],
    }));
  };

  const properties = Object.entries(entity.properties || {});

  return (
    <div className="space-y-6">
      {/* Properties Section */}
      {properties.length > 0 && (
        <Card className="bg-white shadow-md">
          <CardHeader>
            <h3 className="text-lg font-bold text-gray-800">
              Eigenschappen
            </h3>
          </CardHeader>
          <Divider />
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {properties.map(([key, value]) => {
                const isMatch = searchQuery ? key.toLowerCase().includes(searchQuery.toLowerCase()) : false;

                return (
                  <PropertyCard
                    key={key}
                    name={key}
                    value={value}
                    tableName={entity.tableName}
                    highlighted={isMatch}
                    registerMatchRef={isMatch ? registerMatchRef : undefined}
                  />
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Relations Section */}
      {entity.relations && entity.relations.length > 0 && (
        <Card className="bg-gradient-to-br from-purple-100 to-pink-100 shadow-md">
          <CardHeader>
            <h3 className="text-lg font-bold text-purple-900">
              Relaties ({entity.relations.length})
            </h3>
          </CardHeader>
          <Divider className="bg-purple-300" />
          <CardBody>
            <div className="space-y-3">
              {entity.relations.map((relation, idx) => (
                <RelationCard
                  key={`${relation.relationName}-${idx}`}
                  relation={relation}
                  expanded={expandedRelations[relation.relationName] || false}
                  onToggleExpand={() => toggleRelation(relation.relationName)}
                  depth={0}
                  searchQuery={searchQuery}
                  registerMatchRef={registerMatchRef}
                />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* No Relations Message */}
      {(!entity.relations || entity.relations.length === 0) && (
        <Card className="bg-gray-50 shadow-sm">
          <CardBody className="text-center py-8">
            <p className="text-gray-500 italic">
              Deze entiteit heeft geen relaties
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default EntityTreeView;
